import React, { useState, useEffect, useCallback, useRef } from "react";
import { Document, Page } from "react-pdf";
import { Button } from "@/dentlab/src/components/ui/button";
import {
	ChevronLeft,
	ChevronRight,
	Loader2,
	ZoomIn,
	ZoomOut,
	Maximize,
} from "lucide-react";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "@/lib/utils/pdf-worker";
import { Logger } from "@/lib/logger/Logger";

interface VendorPdfPreviewProps {
	url: string;
}

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const TOOLBAR_HEIGHT = 64;
const PADDING = 40;

export const VendorPdfPreview: React.FC<VendorPdfPreviewProps> = ({ url }) => {
	const [numPages, setNumPages] = useState<number | null>(null);
	const [pageNumber, setPageNumber] = useState(1);
	const [zoom, setZoom] = useState(1);
	const [containerWidth, setContainerWidth] = useState<number | null>(null);
	const [containerHeight, setContainerHeight] = useState<number | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const initialFitApplied = useRef(false);

	const measureContainer = useCallback(() => {
		if (!containerRef.current) return;
		setContainerWidth(containerRef.current.clientWidth - PADDING);
		setContainerHeight(
			containerRef.current.clientHeight - TOOLBAR_HEIGHT - PADDING
		);
	}, []);

	useEffect(() => {
		measureContainer();
		const observer = new ResizeObserver(measureContainer);
		if (containerRef.current) observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, [measureContainer]);

	useEffect(() => {
		setIsLoading(true);
		setHasError(false);
		setPageNumber(1);
		initialFitApplied.current = false;
	}, [url]);

	const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
		setNumPages(numPages);
		setIsLoading(false);
		setHasError(false);
	};

	const onPageLoadSuccess = ({
		width,
		height,
	}: {
		width: number;
		height: number;
	}) => {
		if (initialFitApplied.current || !containerWidth || !containerHeight)
			return;
		initialFitApplied.current = true;
		const fitHeightWidth = containerHeight * (width / height);
		const fitZoom = fitHeightWidth / containerWidth;
		setZoom(Math.min(fitZoom, 1));
	};

	const onDocumentLoadError = (error: Error) => {
		Logger.error("PDF loading error:" + error);
		setHasError(true);
		setTimeout(() => setIsLoading(false), 2000);
	};

	const goToPrevPage = () => setPageNumber((p) => Math.max(p - 1, 1));
	const goToNextPage = () =>
		setPageNumber((p) => Math.min(p + 1, numPages || 1));

	const handleZoomIn = () =>
		setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
	const handleZoomOut = () =>
		setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM));
	const handleFitWidth = () => setZoom(1);

	const renderWidth =
		containerWidth !== null ? containerWidth * zoom : undefined;

	return (
		<div
			ref={containerRef}
			className="relative h-full w-full flex flex-col"
		>
			<div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-white/80 backdrop-blur-sm border border-stone-200 shadow-sm rounded-lg p-2 flex items-center gap-2">
				{numPages && numPages > 1 && (
					<>
						<Button
							variant="ghost"
							size="icon"
							onClick={goToPrevPage}
							className="text-stone-600 hover:bg-stone-100 h-8 w-8"
							disabled={pageNumber <= 1}
							aria-label="Vorherige Seite"
							data-testid="vendor-pdf-prev-page"
						>
							<ChevronLeft className="h-4 w-4" />
						</Button>
						<div className="text-stone-700 text-sm font-medium px-2 text-center whitespace-nowrap">
							{pageNumber} von {numPages}
						</div>
						<Button
							variant="ghost"
							size="icon"
							onClick={goToNextPage}
							className="text-stone-600 hover:bg-stone-100 h-8 w-8"
							disabled={pageNumber >= numPages}
							aria-label="Nächste Seite"
							data-testid="vendor-pdf-next-page"
						>
							<ChevronRight className="h-4 w-4" />
						</Button>
						<div className="w-px h-6 bg-stone-200 mx-1" />
					</>
				)}

				<Button
					variant="ghost"
					size="icon"
					onClick={handleZoomOut}
					className="text-stone-600 hover:bg-stone-100 h-8 w-8"
					disabled={zoom <= MIN_ZOOM}
					aria-label="Verkleinern"
					data-testid="vendor-pdf-zoom-out"
				>
					<ZoomOut className="h-4 w-4" />
				</Button>

				<div className="text-stone-700 text-sm font-medium px-3 min-w-[60px] text-center">
					{Math.round(zoom * 100)}%
				</div>

				<Button
					variant="ghost"
					size="icon"
					onClick={handleZoomIn}
					className="text-stone-600 hover:bg-stone-100 h-8 w-8"
					disabled={zoom >= MAX_ZOOM}
					aria-label="Vergrössern"
					data-testid="vendor-pdf-zoom-in"
				>
					<ZoomIn className="h-4 w-4" />
				</Button>

				<div className="w-px h-6 bg-stone-200 mx-1" />

				<Button
					variant="ghost"
					size="icon"
					onClick={handleFitWidth}
					className={`text-stone-600 hover:bg-stone-100 h-8 w-8 ${
						zoom === 1 ? "bg-stone-100" : ""
					}`}
					aria-label="An Breite anpassen"
					data-testid="vendor-pdf-fit-width"
				>
					<Maximize className="h-4 w-4" />
				</Button>
			</div>

			<div className="flex-1 overflow-auto min-h-0">
				<div className="flex justify-center p-5 pt-16">
					<Document
						file={url}
						onLoadSuccess={onDocumentLoadSuccess}
						onLoadError={onDocumentLoadError}
						loading={
							<div className="flex items-center justify-center py-20">
								<Loader2 className="h-8 w-8 animate-spin text-stone-400" />
							</div>
						}
						error={
							!isLoading ? (
								<div className="text-red-500">
									Fehler beim Laden der PDF-Datei
								</div>
							) : (
								<div className="flex items-center justify-center py-20">
									<Loader2 className="h-8 w-8 animate-spin text-stone-400" />
								</div>
							)
						}
					>
						<Page
							pageNumber={pageNumber}
							width={renderWidth}
							renderTextLayer={true}
							renderAnnotationLayer={true}
							className="shadow-lg"
							onLoadSuccess={onPageLoadSuccess}
						/>
					</Document>
				</div>
			</div>
		</div>
	);
};
